// Client-side helper for listing available broker_inventory CSVs for a stock
// directly from Azure Blob Storage (trading-data-v2/broker_transaction/broker_inventory/{ticker}/),
// using the same container-level read+list SAS token as stockMetadataBlob.ts
// (GET /api/watchlist/sas-token). Replaces the previous
// GET /api/broker-inventory/brokers/:stockCode backend round-trip.

import { api } from '../services/api';

const BI_PREFIX = 'broker_transaction/broker_inventory/';

/**
 * List broker codes that have inventory data for a given ticker, derived from
 * an Azure "List Blobs" call against broker_transaction/broker_inventory/{ticker}/.
 */
export async function listBrokerInventoryBrokers(ticker: string): Promise<string[]> {
  const tokenRes = await api.getStockMetadataSasToken();
  if (!tokenRes.success || !tokenRes.data) {
    throw new Error(tokenRes.error || 'Failed to get stock metadata SAS token');
  }
  const { containerUrl, sasToken } = tokenRes.data;
  const prefix = `${BI_PREFIX}${ticker.toUpperCase()}/`;

  const isDev = (import.meta as any).env?.DEV || false;
  const listUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/?restype=container&comp=list&prefix=${prefix}&${sasToken}`
    : `${containerUrl}?restype=container&comp=list&prefix=${prefix}&${sasToken}`;

  const response = await fetch(listUrl);
  if (!response.ok) throw new Error(`HTTP ${response.status}: failed to list broker inventory blobs`);
  const xmlText = await response.text();

  const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
  const brokers = new Set<string>();
  for (const blob of Array.from(doc.getElementsByTagName('Blob'))) {
    const name = blob.getElementsByTagName('Name')[0]?.textContent || '';
    if (!name.startsWith(prefix)) continue;
    const fileName = name.slice(prefix.length).replace(/\.csv$/i, '');
    if (fileName) brokers.add(fileName.toUpperCase());
  }

  return Array.from(brokers).sort();
}
